import React, { CSSProperties, useEffect, useRef, useState, Fragment, useCallback } from 'react';
import { PaperProps as MuiPaperProps } from '@material-ui/core/Paper';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';
import styled from 'styled-components';
import { PopoverOrigin } from '@material-ui/core/Popover/Popover';
import isEqual from 'react-fast-compare';
import { SystemIcon } from '../Icon/SystemIcon';
import { SystemIconButton } from '../Icon/SystemIconButton';
import { Keys } from '../../utils/keysUtil';
import { StrictUnion } from '../../types';
import { filterNotEmpty } from '../../utils/listHelpers';
import { tokens } from '../../theme';
import { Divider } from '../Divider/Divider';
import { MenuListItemText } from '../MenuList/MenuListItemText';
import { HelperText } from '../Form/HelperText';
import { FormControl } from '../Form/FormControl';
import { InputAdornment } from '../Input/InputAdornment';
import { OptionTypeWithCode, OptionTypeWithExtraMenuItemLabel } from './types';

export function SelectWithFilter<VALUE = string>({
  placeholder,
  required,
  error,
  helpText,
  label,
  dataTest,
  style,
  className,
  id,
  filterProps,
  menuProps,
  paperProps,
  fullWidth,
  hideCodes,
  options,
  disabled,
  forceOpen,
  onClose,
  // It is required to use props here for: onChange, value and multiple
  // Otherwise TS has an issue to detect correct types for: onChange, value and multiple
  ...props
}: SelectWithFilterProps<VALUE>) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [filterTerm, setFilterTerm] = useState('');
  const filterRef = useRef<HTMLInputElement | null>(null);
  const filterClearRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLInputElement | null>(null);
  const inputRef = useRef<HTMLDivElement | null>(null);
  const firstMenuItem = useRef<HTMLLIElement | null>(null);
  const textFieldWidth: number = inputRef.current?.clientWidth ?? 300;
  const visibleOptions = filteredOptions(options, filterTerm);

  const valueOptionIndex = !props.multiple ? options.findIndex((option) => isSelected(option, props.value)) : undefined;
  const selectedOptionIndex = !props.multiple && visibleOptions.findIndex((option) => isSelected(option, props.value));
  const valueOption = typeof valueOptionIndex === 'number' ? options[valueOptionIndex] : undefined;
  const isSelectedOption = selectedOptionIndex !== -1;

  // MENU MANIPULATION
  const menuClose = (forceClose: boolean) => {
    if (props.multiple && !forceClose) return;
    onClose?.();
    setAnchorEl(null);
  };

  const menuOpen = useCallback((element: HTMLElement | null) => {
    setFilterTerm('');
    setAnchorEl(element);
  }, []);

  // DATA MANIPULATION

  const valueChange = (option: OptionTypeWithExtraMenuItemLabel<VALUE> | undefined) => {
    if (props.multiple) {
      const optionAlreadyExist = props.value?.find((el) => isEqual(el, option?.value));
      let optionToSet: VALUE[] | undefined = [];
      if (optionAlreadyExist) {
        optionToSet = props.value?.filter((el) => !isEqual(el, option?.value));
      } else {
        optionToSet = filterNotEmpty([option?.value, ...(props.value || [])]);
      }
      (props.onChange as MultiInterface<VALUE>['onChange'])(optionToSet ?? undefined);
    }
    if (!props.multiple) {
      (props.onChange as SingleInterface<VALUE>['onChange'])(option?.value ?? undefined);
      menuClose(false);
    }
  };

  // EVENT HANDLERS
  const stopEvent = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };
  // INPUT
  const inputKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (Keys.isOneOf(e, ['enter'])) {
      menuOpen(e.currentTarget);
    }
  };

  useEffect(() => {
    if (inputRef && forceOpen) {
      menuOpen(inputRef?.current);
    }
  }, [forceOpen, menuOpen]);

  const inputClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      menuOpen(e.currentTarget);
    }
  };

  // FILTER INPUT
  const filterInputFocus = () => {
    filterRef.current?.focus();
  };

  const filterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (Keys.isOneOf(e, ['arrowUp'])) {
      menuClose(false);
    }
    if (Keys.isOneOf(e, ['tab', 'arrowDown'])) {
      if (filterTerm) {
        filterClearFocus();
      } else {
        firstMenuItemFocus();
      }
      stopEvent(e);
    }
    if (Keys.is(e, 'escape')) {
      menuClose(true);
    }
    e.stopPropagation();
  };

  const filterClearKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (Keys.isOneOf(e, ['tab', 'arrowDown'])) {
      firstMenuItemFocus();
    }
    if (Keys.isOneOf(e, ['arrowUp'])) {
      filterInputFocus();
    }
    if (Keys.isOneOf(e, ['enter'])) {
      filterClearClick();
    }
    stopEvent(e);
  };

  const filterClearClick = () => {
    setFilterTerm('');
    filterInputFocus();
  };

  const filterClearFocus = () => {
    filterClearRef?.current?.focus();
  };

  // MENU ITEMS
  const firstMenuItemFocus = () => {
    firstMenuItem.current?.focus();
  };

  const menuItemKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, index: number | undefined) => {
    if (Keys.is(e, 'arrowDown') && index === visibleOptions.length - 1) {
      firstMenuItemFocus();
      stopEvent(e);
    }
    if (Keys.is(e, 'arrowUp') && index === 0) {
      if (filterTerm) {
        filterClearFocus();
      } else {
        filterInputFocus();
      }
      stopEvent(e);
    }
    if (Keys.is(e, 'enter') && typeof index === 'number') {
      valueChange(visibleOptions[index]);
    }
  };

  const menuItemClick = (option: OptionTypeWithCode<VALUE> | undefined) => {
    valueChange(option);
  };

  const placeholderToDisplay = props.multiple
    ? mapValueToLabel(props.value, options, placeholder, props.mapValueToLabel)
    : placeholder;

  return (
    <FormControl
      required={required}
      error={!!error}
      fullWidth={fullWidth}
      dataTest={`${dataTest ?? 'select'}-form-control`}
    >
      <TextField
        label={label}
        required={required}
        InputLabelProps={{ shrink: true }}
        fullWidth={fullWidth}
        error={!!error}
        onClick={inputClick}
        onKeyDown={inputKeyDown}
        ref={inputRef}
        placeholder={placeholderToDisplay}
        aria-readonly="true"
        aria-controls={`${id}-menu`}
        aria-haspopup="true"
        data-test={dataTest}
        disabled={disabled}
        className={`${className} select`}
        value={
          valueOption ? `${valueOption?.label}${!hideCodes && valueOption?.code ? ` ${valueOption.code}` : ''}` : ''
        }
        InputProps={{
          type: 'text',
          disableUnderline: true,
          readOnly: true,
          style,
          startAdornment: valueOption?.icon ? (
            <InputAdornment position="start" style={{ paddingLeft: 8 }}>
              {valueOption.icon}
            </InputAdornment>
          ) : undefined,
          endAdornment: (
            <InputAdornment position="end">
              <SystemIcon name="arrow_drop_down" />
            </InputAdornment>
          )
        }}
      />
      <Menu
        elevation={0}
        getContentAnchorEl={null}
        PaperProps={
          {
            'data-test': 'select-with-filter-menu',
            className: paperProps?.className,
            style: {
              minWidth: textFieldWidth,
              border: `1px solid ${tokens.colorSurfaceVariant}`,
              boxShadow: `0px 3px 10px rgba(0, 0, 0, 0.06)`,
              marginTop: -1,
              ...paperProps?.style
            }
          } as Partial<MuiPaperProps>
        }
        className={menuProps?.className}
        anchorOrigin={
          menuProps?.anchorOrigin ?? {
            vertical: 'bottom',
            horizontal: 'center'
          }
        }
        transformOrigin={
          menuProps?.transformOrigin ?? {
            vertical: 'top',
            horizontal: 'center'
          }
        }
        style={{
          border: `1px solid ${tokens.colorSurfaceVariant}`,
          boxShadow: `0px 3px 10px rgba(0, 0, 0, 0.06)`,
          marginTop: -1,
          ...menuProps?.style
        }}
        id={`${id}-menu`}
        anchorEl={anchorEl}
        open={!!anchorEl}
        ref={menuRef}
        onClose={() => menuClose(true)}
      >
        <StyledFilterWrapper
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <TextField
            value={filterTerm}
            label={filterProps?.label ?? ''}
            InputLabelProps={{ shrink: true }}
            inputRef={filterRef}
            onKeyDown={filterKeyDown}
            data-test={`${dataTest ?? 'select'}-search`}
            className={filterProps?.className}
            InputProps={{
              disableUnderline: true,
              inputMode: 'search',
              style: filterProps?.style,
              startAdornment: (
                <InputAdornment position="start">
                  <SystemIcon name="search" />
                </InputAdornment>
              ),
              endAdornment: filterTerm ? (
                <InputAdornment position="end">
                  <SystemIconButton
                    iconName="close"
                    onClick={filterClearClick}
                    onKeyDown={filterClearKeyDown}
                    ref={filterClearRef}
                  />
                </InputAdornment>
              ) : null
            }}
            fullWidth
            autoFocus
            placeholder={filterProps?.placeholder ?? ''}
            onChange={(e) => setFilterTerm(e.target.value)}
          />
        </StyledFilterWrapper>
        <li>
          <StyledMenuItemWrapper>
            {!required && (
              <MenuItem
                onClick={() => menuItemClick(undefined)}
                ref={firstMenuItem}
                selected={!isSelectedOption}
                onKeyDown={(e) => menuItemKeyDown(e, undefined)}
              >
                <MenuListItemText primary="None" />
              </MenuItem>
            )}

            {visibleOptions.map((option, index, array) => {
              const isSelected = props.multiple
                ? !!props.value?.find((el) => isEqual(el, option.value))
                : index === selectedOptionIndex;
              const isFirstElOfGroup = option.group && array[index - 1]?.group !== option.group;
              return (
                <Fragment key={option.label + (option.code ?? '') + index}>
                  {isFirstElOfGroup && (
                    <>
                      <Divider />
                      <StyledGroupElement>{option.group}</StyledGroupElement>
                    </>
                  )}
                  <MenuItem
                    onClick={() => menuItemClick(option)}
                    ref={index === 0 && required ? firstMenuItem : undefined}
                    selected={isSelected}
                    onKeyDown={(e) => menuItemKeyDown(e, index)}
                  >
                    {!!option.icon && <ListItemIcon>{option.icon}</ListItemIcon>}
                    <ListItemText
                      primary={`${option.menuItemLabel ? `${option.menuItemLabel} ` : ''}${option.label}`}
                      secondary={!hideCodes ? option.code : undefined}
                    />
                    {props.multiple && isSelected && <SystemIcon name="check" />}
                  </MenuItem>
                </Fragment>
              );
            })}
          </StyledMenuItemWrapper>
        </li>
      </Menu>
      <HelperText error={!!error} dataTest={dataTest} id={id}>
        {error || helpText}
      </HelperText>
    </FormControl>
  );
}

const StyledGroupElement = styled.div`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  padding: 16px 16px 8px 16px;
`;

const StyledFilterWrapper = styled.li`
  padding: 8px 16px 16px 16px;
`;

const StyledMenuItemWrapper = styled.ul`
  padding: 0;
  overflow: hidden auto;
  max-height: 300px;
`;

const mapValueToLabel = <VALUE extends unknown = string>(
  values?: VALUE[] | null,
  options?: OptionTypeWithExtraMenuItemLabel<VALUE>[],
  placeholder?: string,
  mapper?: (values: VALUE[]) => string
): string => {
  if (!values || values.length === 0) return placeholder || 'Select...';
  if (!mapper)
    return values
      .map((value) => options?.find((el) => isEqual(el.value, value))?.label)
      .filter(Boolean)
      .join(',');
  return mapper(values);
};

function filteredOptions<V, T extends OptionTypeWithExtraMenuItemLabel<V>>(options: T[], term: string): T[] {
  const lowerCaseTerm = term.toLowerCase();
  return options
    .filter(
      (option) =>
        includes(lowerCaseTerm, option.label) ||
        includes(lowerCaseTerm, option.menuItemLabel) ||
        includes(lowerCaseTerm, option.code)
    )
    .sort((a, b) => a.group?.localeCompare(b?.group ?? '') ?? 0);
}

function isSelected<V, T extends OptionTypeWithExtraMenuItemLabel<V>>(option: T, value?: V): boolean {
  if (!value || !option.value) {
    return false;
  }
  try {
    return JSON.stringify(option.value) === JSON.stringify(value);
  } catch (e) {
    return false;
  }
}

function includes(searchTerm: string = '', label?: string): boolean {
  const lowerCaseTerm = searchTerm.toLowerCase();
  return (label ?? '').toLowerCase().includes(lowerCaseTerm);
}

export type SelectWithFilterProps<VALUE = string> = StrictUnion<MultiInterface<VALUE> | SingleInterface<VALUE>>;

interface MultiInterface<VALUE> extends SharedInterface<VALUE> {
  value: VALUE[] | undefined | null;
  onChange(value?: VALUE[]): void;
  multiple: true;
  /*! Provide this function to map selected items to displayed value. */
  mapValueToLabel?: (value: VALUE[]) => string;
}

interface SingleInterface<VALUE> extends SharedInterface<VALUE> {
  value: VALUE | undefined | null;
  onChange(value?: VALUE): void;
  multiple?: false;
}

interface SharedInterface<VALUE> {
  label?: string;
  placeholder?: string;
  helpText?: string;
  touched?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  dataTest?: string;
  hideCodes?: boolean;
  className?: string;
  style?: CSSProperties;
  filterProps?: {
    placeholder?: string;
    label?: string;
    className?: string;
    style?: CSSProperties;
  };
  menuProps?: {
    style?: CSSProperties;
    className?: string;
    anchorOrigin?: PopoverOrigin;
    transformOrigin?: PopoverOrigin;
  };
  paperProps?: {
    style?: CSSProperties;
    className?: string;
  };
  id?: string;
  options: OptionTypeWithExtraMenuItemLabel<VALUE>[];
  onClose?: () => void;
  forceOpen?: boolean;
}
